import { useTranslation } from "react-i18next";
import BorrowTransactionForm from "./BorrowTransactionForm";
import CollectTransactionForm from "./CollectTransactionForm";
import ExpenseTransactionForm from "./ExpenseTransactionForm";
import IncomeTransactionForm from "./IncomeTransactionForm";
import InstallmentConversionForm from "./InstallmentConversionForm";
import LendTransactionForm from "./LendTransactionForm";
import RepayTransactionForm from "./RepayTransactionForm";
import TransferTransactionForm from "./TransferTransactionForm";
import { TransactionType } from "./types";
import { TransactionFormState } from "../hooks/useTransactionFormState";

interface TransactionFormContentProps {
  formState: TransactionFormState;
}

const TransactionFormContent = ({ formState }: TransactionFormContentProps) => {
  const { t } = useTranslation(['transactions']);

  const {
    currentTab,
    loanType,
    creditCardType,
    transaction,
    amount,
    description,
    datePicker,
    openDatePicker,
    spendingCategory,
    incomeCategory,
    expenseWallet,
    incomeWallet,
    transferSourceWallet,
    selectDestinationWallet,
    numberOfInstallments,
    loanWallet,
    repaymentWallet,
    initialLoanWallet,
    initialDebtWallet,
    setSpendingCategory,
    setLoanWallet,
    setDatePicker,
    setDescription,
    setRepaymentWallet,
    setRepaymentDate,
    setLendWallets,
    setLendExpenseWallet,
    setSourceWallet,
    setOpenDialogSelectedExpenseCategory,
    setOpenDialogSelectedIncomeCategory,
    setOpenDialogSelectWallets,
    setOpenDialogSelectDestinationWallets,
    setOpenDatePicker,
    setNumberOfInstallments,
    descriptionInputRef,
    installmentTermInputRef,
  } = formState;

  // Render expense form
  const renderExpenseForm = () => (
    <ExpenseTransactionForm
      spendingCategory={spendingCategory}
      expenseWallet={expenseWallet}
      datePicker={datePicker}
      description={description}
      openDatePicker={openDatePicker}
      setOpenDialogSelectedExpenseCategory={setOpenDialogSelectedExpenseCategory}
      setOpenDialogSelectWallets={setOpenDialogSelectWallets}
      setOpenDatePicker={setOpenDatePicker}
      setDescription={setDescription}
      setDatePicker={setDatePicker}
      descriptionInputRef={descriptionInputRef}
      disableWalletSelection={transaction?.type === 'borrow-expense'}
    />
  );

  // Render income form
  const renderIncomeForm = () => (
    <IncomeTransactionForm
      incomeCategory={incomeCategory}
      incomeWallet={incomeWallet}
      datePicker={datePicker}
      description={description}
      openDatePicker={openDatePicker}
      setOpenDialogSelectedIncomeCategory={setOpenDialogSelectedIncomeCategory}
      setOpenDialogSelectWallets={setOpenDialogSelectWallets}
      setOpenDatePicker={setOpenDatePicker}
      setDescription={setDescription}
      setDatePicker={setDatePicker}
      descriptionInputRef={descriptionInputRef}
    />
  );

  // Render transfer form
  const renderTransferForm = (sourceLabel?: string, destLabel?: string) => (
    <TransferTransactionForm
      transferSourceWallet={transferSourceWallet}
      selectDestinationWallet={selectDestinationWallet}
      datePicker={datePicker}
      description={description}
      openDatePicker={openDatePicker}
      setOpenDialogSelectWallets={setOpenDialogSelectWallets}
      setOpenDialogSelectDestinationWallets={setOpenDialogSelectDestinationWallets}
      setOpenDatePicker={setOpenDatePicker}
      setDescription={setDescription}
      setDatePicker={setDatePicker}
      descriptionInputRef={descriptionInputRef}
      sourceWalletLabel={sourceLabel}
      destinationWalletLabel={destLabel}
    />
  );

  // Render loan forms
  const renderLoanForm = () => {
    if (currentTab !== TransactionType.LOAN && currentTab !== TransactionType.CREDIT_CARD) {
      return null;
    }

    // Credit card types
    if (currentTab === TransactionType.CREDIT_CARD) {
      if (creditCardType === 'pay_balance') {
        return renderTransferForm(
          t('transfer_wallet', { ns: 'transactions' }),
          t('pay_to_card', { ns: 'transactions', defaultValue: 'Thanh toán cho thẻ' })
        );
      }
      if (creditCardType === 'cash_advance') {
        return renderTransferForm(
          t('credit_card_wallet', { ns: 'transactions' }),
          t('income_wallet', { ns: 'transactions' })
        );
      }
      if (creditCardType === 'convert_installment') {
        return (
          <InstallmentConversionForm
            creditCardWallet={selectDestinationWallet}
            datePicker={datePicker}
            description={description}
            openDatePicker={openDatePicker}
            numberOfInstallments={numberOfInstallments}
            setOpenDialogSelectWallets={setOpenDialogSelectDestinationWallets}
            setOpenDatePicker={setOpenDatePicker}
            setDescription={setDescription}
            setDatePicker={setDatePicker}
            setNumberOfInstallments={setNumberOfInstallments}
            descriptionInputRef={descriptionInputRef}
            installmentTermInputRef={installmentTermInputRef}
          />
        );
      }
      return null;
    }

    // Loan types
    switch (loanType) {
      case 'borrow':
        return (
          <BorrowTransactionForm
            amount={amount}
            initialLoanWallet={loanWallet || initialLoanWallet}
            initialRepayWallet={repaymentWallet}
            initialDatePicker={datePicker}
            initialDescription={description}
            setLoanWallet={setLoanWallet}
            setDatePicker={setDatePicker}
            setDescription={setDescription}
            setRepayWallet={setRepaymentWallet}
            setRepaymentDate={setRepaymentDate}
            // initialLoanWallet={initialLoanWallet}
          />
        );
      case 'lend':
        return (
          <LendTransactionForm
            amount={amount}
            // initialRepayWallet={repaymentWallet}
            initialDatePicker={datePicker}
            initialDescription={description}
            setLendWallets={setLendWallets}
            setExpenseWallet={setLendExpenseWallet}
            setSourceWallet={setSourceWallet}
            setDatePicker={setDatePicker}
            setDescription={setDescription}
            setOpenDialogSelectedExpenseCategory={setOpenDialogSelectedExpenseCategory}
            spendingCategory={spendingCategory}
            setSpendingCategory={setSpendingCategory}
            initialSourceWallet={loanWallet || initialDebtWallet}
          />
        );
      case 'repay':
        return (
          <RepayTransactionForm
            amount={amount}
            initialLoanWallet={loanWallet}
            initialRepayWallet={repaymentWallet}
            initialDatePicker={datePicker}
            initialDescription={description}
            setLoanWallet={setLoanWallet}
            setRepayWallet={setRepaymentWallet}
            setDatePicker={setDatePicker}
            setDescription={setDescription}
          />
        );
      case 'collect':
        return (
          <CollectTransactionForm
            amount={amount}
            initialLoanWallet={loanWallet}
            initialRepayWallet={repaymentWallet}
            initialDatePicker={datePicker}
            initialDescription={description}
            setLoanWallet={setLoanWallet}
            setRepayWallet={setRepaymentWallet}
            setDatePicker={setDatePicker}
            setDescription={setDescription}
          />
        );
      case 'borrow-expense':
        return (
          <ExpenseTransactionForm
            spendingCategory={spendingCategory}
            expenseWallet={expenseWallet}
            datePicker={datePicker}
            description={description}
            openDatePicker={openDatePicker}
            setOpenDialogSelectedExpenseCategory={setOpenDialogSelectedExpenseCategory}
            setOpenDialogSelectWallets={setOpenDialogSelectWallets}
            setOpenDatePicker={setOpenDatePicker}
            setDescription={setDescription}
            setDatePicker={setDatePicker}
            descriptionInputRef={descriptionInputRef}
            isBorrowExpense={true}
          />
        );
      default:
        return null;
    }
  };

  return {
    renderExpenseForm,
    renderIncomeForm,
    renderTransferForm,
    renderLoanForm,
  };
};

export default TransactionFormContent;
